import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import React from "react";
import Loader from "../../components/Loader";

// Dry run result for one shift: what "Run check" would raise. Nothing here
// creates tickets.
function PreviewDialog({ open, onClose, loading, result, date, shift }) {
  const machines = result?.machines ?? [];
  const toRaise = machines.filter((machine) => !machine.alreadyTicketed).length;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        Preview: {date} Shift {shift}
        <Typography variant="body2" color="text.secondary">
          Dry run. No tickets are created and no SMS is sent.
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        {loading || !result ? (
          <Loader />
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Typography>
              {result.rows} rows read · {result.activeLimits} active fields ·{" "}
              <b>{toRaise}</b> tickets would be raised
              {machines.length > toRaise &&
                ` (${machines.length - toRaise} already ticketed)`}
            </Typography>

            {result.rows === 0 && (
              <Alert severity="info">
                The procedure returned no rows for this shift.
              </Alert>
            )}
            {result.activeLimits === 0 && (
              <Alert severity="info">
                No active field limits: nothing would be ticketed.
              </Alert>
            )}
            {result.unmatchedFields?.length > 0 && (
              <Alert severity="warning">
                These fields match no column from the procedure, so they are
                never checked: <b>{result.unmatchedFields.join(", ")}</b>
              </Alert>
            )}

            {machines.length > 0 && (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Department</TableCell>
                      <TableCell>Machine</TableCell>
                      <TableCell>Out of limit</TableCell>
                      <TableCell>Result</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {machines.map((machine) => (
                      <TableRow key={`${machine.dept}|${machine.machine}`}>
                        <TableCell>{machine.dept || "-"}</TableCell>
                        <TableCell>{machine.machine}</TableCell>
                        <TableCell sx={{ whiteSpace: "pre-wrap" }}>
                          {machine.lines.join("\n\n")}
                        </TableCell>
                        <TableCell>
                          {machine.alreadyTicketed ? (
                            <Chip size="small" label="Already ticketed" />
                          ) : (
                            <Chip
                              size="small"
                              color="warning"
                              label="Would raise ticket"
                            />
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
            {result.rows > 0 && machines.length === 0 && (
              <Alert severity="success">
                Every machine is within its limits for this shift.
              </Alert>
            )}

            {result.columns?.length > 0 && (
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Columns returned by the procedure
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {result.columns.map((column) => (
                    <Chip key={column} size="small" variant="outlined" label={column} />
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

export default PreviewDialog;
